import { stockApi, webApi } from '$api'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import {
  IPurchaseOrder,
  PurchaseOrder,
  OrderTemplate,
  PurchaseOrderPatchRequest,
  transformWebApiPurchaseOrderOnGet,
  WebApiPurchaseOrder,
  IPurchaseOrderCommunicationLog,
} from '$models'
import {
  ApiResponse,
  FileResponse,
  SendPurchaseOrderResponse,
} from '@loadedhub/loaded-stock-api-sdk'
import { PurchaseOrderQueryConditions } from 'src/components/routes/stock/ordering/purchase-orders/slice'
import { API_DATE_FORMAT } from '$constants'

enum QueryKeys {
  PurchaseOrders = 'purchaseOrders',
}

/**
 * Get all purchase orders
 * @returns an array of purchase orders
 */
export const getPurchaseOrders = async (
  query?: string
): Promise<IPurchaseOrder[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['purchaseOrders', { query }],
    queryFn: async () => {
      return await stockApi.getPurchaseOrders(query)
    },
  })
}

export const getPurchaseOrdersHistory = async (
  queryConditions: PurchaseOrderQueryConditions
): Promise<ApiResponse<PurchaseOrder[]>> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.PurchaseOrders, queryConditions],
    queryFn: async ({ signal }) => {
      return stockApi.getPurchaseOrderHistory(
        queryConditions.start?.format(API_DATE_FORMAT),
        queryConditions.end?.format(API_DATE_FORMAT),
        queryConditions.referenceNumber,
        queryConditions.supplierName,
        queryConditions.page,
        queryConditions.pageSize,
        queryConditions.sort,
        signal
      )
    },
  })
}

/**
 * Get all purchase orders
 * @returns an array of purchase orders
 */
export const getUnreceivedPurchaseOrdersSummary = async (
  query?: string
): Promise<IPurchaseOrder[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['unreceivedPurchaseOrders', { query }],
    queryFn: async () => {
      return await stockApi.getUnreceivedPurchaseOrdersSummary()
    },
  })
}

/**
 * Get a specific purchase orders
 * @returns a purchase orders
 */
export const getPurchaseOrder = async (
  id: string
): Promise<IPurchaseOrder | null> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['purchaseOrder', id],
    queryFn: async () => {
      return await stockApi.getPurchaseOrder(id)
    },
  })
}

/**
 * Update a purchase order (PUT)
 * @returns the updated purchase order
 */
export const updatePurchaseOrder = async (
  purchaseOrderId: string,
  body?: PurchaseOrder
): Promise<IPurchaseOrder | null> => {
  return await stockApi.updatePurchaseOrder(purchaseOrderId, body)
}

/**
 * Update a purchase order
 */
// This is going to be retired by the end of the PO work
// so there is not point on spending time migrating it
export const updatePurchaseOrderWebApi = async (
  purchaseOrder: WebApiPurchaseOrder
): Promise<void> => {
  await webApi.update(
    `r/StockPurchaseOrders/${purchaseOrder.id}`,
    purchaseOrder
  )
}

/**
 * Get a purchase order
 * @returns an purchase order of the given ID
 */
export const getWebApiPurchaseOrder = async (
  id: string
): Promise<WebApiPurchaseOrder | null> => {
  const purchaseOrder = await webApi.get<WebApiPurchaseOrder>(
    `r/StockPurchaseOrders/${id}`
  )
  return transformWebApiPurchaseOrderOnGet(purchaseOrder)
}

export const getCommunicationLogs = async (
  id: string
): Promise<IPurchaseOrderCommunicationLog[]> => {
  return await stockApi.getPurchaseOrderCommunicationLogs(id)
}

/**
 * Update purchase order third party invoice id
 * @param purchaseOrderId - purchase order id
 * @param thirdPartyInvoiceId - third party invoice id
 * @returns IPurchaseOrder
 */
export const updateThirdPartyInvoiceId = async (
  purchaseOrderId: string,
  thirdPartyInvoiceId?: string
): Promise<IPurchaseOrder | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.updatePurchaseOrderThirdPartyInvoiceId(
    purchaseOrderId,
    new PurchaseOrderPatchRequest({
      thirdPartyInvoiceId,
    })
  )
}

/**
 * Deletes a purchase order
 * @param purchaseOrderId - The Purchase Order id to be deleted
 * @returns IPurchaseOrder
 */
export const deletePurchaseOrder = async (
  purchaseOrderId: string
): Promise<IPurchaseOrder | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.deletePurchaseOrder(purchaseOrderId)
}

/**
 * Restores a deleted purchase order
 * @param purchaseOrderId - The Purchase Order id to be restored
 * @returns IPurchaseOrder
 */
export const restorePurchaseOrder = async (
  purchaseOrderId: string
): Promise<IPurchaseOrder | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  const purchaseOrder = await stockApi.getPurchaseOrder(purchaseOrderId)
  if (purchaseOrder instanceof PurchaseOrder) {
    return await stockApi.updatePurchaseOrder(
      purchaseOrderId,
      new PurchaseOrder({
        ...purchaseOrder,
        datestampDeleted: undefined,
      })
    )
  }
  return null
}

/**
 * Creates multiple purchase orders
 * @param purchaseOrders - An array of Purchase Orders to be created
 * @returns - An array of created Purchase Orders
 */
export const createPurchaseOrders = async (
  purchaseOrders: PurchaseOrder[]
): Promise<PurchaseOrder[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return await stockApi.createPurchaseOrders(purchaseOrders)
}

/**
 * Creates multiple purchase orders
 * @param orderTemplate - The orderTemplate to be created
 * @returns - The created orderTemplate
 */
export const createOrderTemplate = async (
  orderTemplate: OrderTemplate
): Promise<OrderTemplate | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  const result = await stockApi.createOrderTemplate(orderTemplate)

  return result
}

/**
 * Sends a purchase order to a supplier
 * @param purchaseOrderId - The Purchase Order id to be sent
 * @param supplierEmail - The supplier email address
 * @param updateSupplierEmail - Whether to update the supplier email address
 * @param emailSubject - The email subject
 * @param companyName - The company name
 */
export const sendPurchaseOrderToSupplier = async (
  purchaseOrderId: string,
  supplierEmail: string,
  updateSupplierEmail: boolean,
  emailSubject: string,
  companyName: string
): Promise<SendPurchaseOrderResponse | undefined> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return

  return await stockApi.sendPurchaseOrderToSupplier(
    purchaseOrderId,
    supplierEmail,
    updateSupplierEmail,
    emailSubject,
    companyName
  )
}

/**
 * Creates a pdf from a purchase order
 * @param purchaseOrderId - The Purchase Order id to be used
 */
export const getPurchaseOrderPdf = async (
  purchaseOrderId: string
): Promise<FileResponse> => {
  return singletonQueryClient.fetchQuery({
    queryKey: ['purchaseOrderPdf', { purchaseOrderId }],
    queryFn: async () => {
      return await stockApi.getPurchaseOrderPdf(purchaseOrderId)
    },
  })
}
